import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { getSingleDownloadFile } from '../api'
import { useIsWebviewMode } from 'src/Hooks'
import { sendMessageToReactNative } from '@e3dc-react/shell/Libs/react-native'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { signalDownload } from 'src/Libs/documents/functions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useCallback } from 'react'

export const useDownloadItemActions = (): {
  viewFile: (downloadItemId: number) => Promise<void>
  downloadFile: (downloadItemId: number) => Promise<void>
} => {
  const authFetch = useAuthFetchWithResponse()
  const isWebview = useIsWebviewMode()
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()

  const viewFile = useCallback(
    async (downloadItemId: number): Promise<void> => {
      try {
        const fileLink = await getSingleDownloadFile(downloadItemId, false, authFetch)
        if (fileLink.value) {
          if (isWebview) {
            sendMessageToReactNative({ type: 'external-url-modal', url: fileLink.value.fileInfo.downloadLink })
          } else {
            const link = document.createElement('a')
            link.href = fileLink.value.fileInfo.downloadLink
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            link.remove()
          }
        }
      } catch (error) {
        console.error('Error fetching file link:', error)
        enqueueErrorStack(t('documents.download-item.error'))
      }
    },
    [isWebview, t, authFetch, enqueueErrorStack]
  )

  const downloadFile = useCallback(
    async (downloadItemId: number): Promise<void> => {
      try {
        const fileLink = await getSingleDownloadFile(downloadItemId, true, authFetch)
        const fileLinkData = fileLink.value

        if (fileLinkData?.fileInfo.fileName) {
          signalDownload(fileLinkData.fileInfo.downloadLink, fileLinkData.fileInfo.fileName, true)
        }
      } catch (error) {
        console.error('Error fetching file link:', error)
        enqueueErrorStack(t('documents.download-item.error'))
      }
    },
    [t, authFetch, enqueueErrorStack]
  )

  return { viewFile, downloadFile }
}
